import React, { useEffect, useState } from 'react';
import { View, Text, Platform, TouchableOpacity } from 'react-native';
import { getApiHost } from '@api/constant';
import { XStack } from 'tamagui';

interface BackendStatusIndicatorProps {
  theme: boolean;
  appVersion?: string;
}

interface HealthResponse {
  status: string;
  timestamp: string;
  uptime?: number;
  port?: number;
  environment?: string;
  version?: string;
}

const BackendStatusIndicator: React.FC<BackendStatusIndicatorProps> = ({ theme, appVersion }) => {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  const [showTooltip, setShowTooltip] = useState(false);
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);

  const checkBackendHealth = async () => {
    try {
      const apiHost = await getApiHost();
      const healthUrl = `${apiHost}/api/health`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(healthUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data: HealthResponse = await response.json();
        setHealthData(data);
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      console.warn('Backend health check failed:', error);
      setConnectionStatus('disconnected');
    }
  };

  useEffect(() => {
    checkBackendHealth();
    const interval = setInterval(() => {
      checkBackendHealth();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return '#10B981';
      case 'disconnected':
        return '#EF4444';
      case 'checking':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getTooltipText = () => {
    const parts = [];

    // Frontend app version (always show if available)
    if (appVersion) {
      parts.push(`Frontend: v${appVersion}`);
    }

    if (connectionStatus === 'connected' && healthData) {
      parts.push(`Status: ${healthData.status}`);
      if (healthData.version) {
        parts.push(`Backend: v${healthData.version}`);
      }
      if (healthData.environment) {
        parts.push(`Env: ${healthData.environment}`);
      }
      if (healthData.uptime) {
        parts.push(`Uptime: ${formatUptime(healthData.uptime)}`);
      }
    } else {
      parts.push(`Status: ${connectionStatus}`);
    }

    return parts.join('\n');
  };

  return (
    <View
      style={{
        position: 'absolute',
        bottom: Platform.OS === 'web' ? 20 : 10,
        left: Platform.OS === 'web' ? 20 : 10,
        zIndex: 10000,
        maxHeight: 70,
        ...Platform.select({
          web: {
            position: 'fixed',
          },
          default: {
            position: 'absolute',
          }
        }),
      }}
    >
      <XStack alignItems="center" space="$3">
        {/* Status Dot with Tooltip */}
        <View
          style={{ position: 'relative' }}
          {...(Platform.OS === 'web' ? {
            onMouseEnter: () => setShowTooltip(true),
            onMouseLeave: () => setShowTooltip(false),
          } : {})}
        >
          <TouchableOpacity
            onPress={() => setShowTooltip(!showTooltip)}
            style={{
              width: 12,
              height: 12,
              borderRadius: 6,
              backgroundColor: getStatusColor(),
              ...Platform.select({
                web: {
                  cursor: 'pointer',
                  boxShadow: `0 0 8px ${getStatusColor()}80`,
                },
                default: {
                  shadowColor: getStatusColor(),
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.8,
                  shadowRadius: 4,
                  elevation: 4,
                }
              }),
            }}
          />

          {/* Tooltip */}
          {showTooltip && (
            <View
              style={{
                position: 'absolute',
                bottom: 20,
                left: 0,
                backgroundColor: theme
                  ? 'rgba(0, 0, 0, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)',
                borderRadius: 8,
                padding: 8,
                minWidth: 180,
                ...Platform.select({
                  web: {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  },
                  default: {
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 8,
                  }
                }),
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  color: theme ? '#F9FAFB' : '#1F2937',
                  fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
                  lineHeight: 16,
                }}
              >
                {getTooltipText()}
              </Text>
            </View>
          )}
        </View>

        {/* App Version */}
        {appVersion && (
          <Text
            style={{
              fontSize: 11,
              color: theme ? '#6B7280' : '#9CA3AF',
              fontWeight: '500',
            }}
          >
            v{appVersion}
          </Text>
        )}
      </XStack>
    </View>
  );
};

export default BackendStatusIndicator;

